// Classification (plan v2 §4.2): what a classification workflow reads and writes. It reads a person's fields from the
// lead registry while it runs (PIN, city, employment …) and keeps only the result as tags (city:metro, emp:salaried,
// score:hot) and batch places. No field value is ever stored, answered or traced: a trace names the rule, not the value.
// Lists (the metro PIN list …) are versioned like voice messages: a new upload is a new version, never an edit.
import { z } from 'zod'
import { Id, IsoDate, IsoDateTime, LeadCode } from './common'
import { TAG_GROUPS } from './leads'
import { TraceStep } from './campaigns'

/** The registry fields a field check may look at, with the values it can compare (bands, never raw amounts). */
export const CLASSIFY_FIELDS = {
  city: [
    'Mumbai',
    'Delhi',
    'Bengaluru',
    'Chennai',
    'Kolkata',
    'Hyderabad',
    'Pune',
    'Ahmedabad',
    'Jaipur',
    'Lucknow',
    'Indore',
    'Nagpur',
  ],
  state: [
    'Maharashtra',
    'Delhi',
    'Karnataka',
    'Tamil Nadu',
    'West Bengal',
    'Telangana',
    'Gujarat',
    'Rajasthan',
    'Uttar Pradesh',
    'Madhya Pradesh',
  ],
  employment: ['salaried', 'self-employed'],
  loan_amount: ['under-1l', '1l-5l', 'over-5l'],
  age_band: ['21-25', '26-35', '36-45', '46-60'],
  /** 2026-10-28: from the person's monthly income (the registry answers only the band, never the amount) */
  income_band: ['under-25k', '25k-50k', '50k-1l', 'over-1l'],
  source: TAG_GROUPS.source,
  partner: TAG_GROUPS.partner,
  /** a tag the workflow (or the lead) already has, e.g. city:metro */
  tag: [],
} as const
export type ClassifyField = keyof typeof CLASSIFY_FIELDS
export const ClassifyField = z.enum([
  'city',
  'state',
  'employment',
  'loan_amount',
  'age_band',
  'income_band',
  'source',
  'partner',
  'tag',
])

/** The tags a classification workflow may add (group:value); adding one replaces that group's old value. */
export const CLASSIFY_TAGS = (['city', 'emp', 'score', 'data'] as const).flatMap((group) =>
  TAG_GROUPS[group].map((value) => `${group}:${value}`)
)

/** The facts a hot-lead score rule may name besides a tag. */
export const SCORE_FACTS = ['returned_7d', 'clicked_link'] as const

export const ClassificationRun = z.object({
  id: Id,
  lead_code: LeadCode,
  workflow_id: Id,
  version: z.number().int().min(1),
  at: IsoDateTime,
  tags: z.array(z.string()).describe('the tags it added'),
  batches: z.array(z.string()).describe('the batches it put the person in (names)'),
  trace: z.array(TraceStep).describe('which rule matched; never a field value'),
})
export type ClassificationRun = z.infer<typeof ClassificationRun>

export const ClassificationRunPage = z.object({
  items: z.array(ClassificationRun).describe('newest first'),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  page_size: z.number().int().min(1),
})
export type ClassificationRunPage = z.infer<typeof ClassificationRunPage>

export const DataList = z.object({
  id: Id,
  list_key: Id.describe('the same for every version of one list'),
  name: z.string(),
  version: z.number().int().min(1),
  latest: z.boolean().describe('the version a list check uses'),
  count: z.number().int().min(0).describe('PIN codes in this version'),
  first: z.array(z.string()).describe('the first few entries, to recognise the file'),
  made_by_name: z.string(),
  at: IsoDate,
  used_by: z.array(z.string()).describe('classification workflows whose live version checks this list'),
})
export type DataList = z.infer<typeof DataList>

const PIN = z.string().regex(/^\d{6}$/, 'A PIN code has 6 digits')
export const UploadListInput = z.object({
  list_key: Id.nullable().describe('null = a new list; else a new version of that list'),
  name: z.string().trim().min(3, 'Give the list a name'),
  pins: z.array(PIN).min(1, 'The file has no PIN code').max(50000, 'At most 50,000 PIN codes'),
})
export type UploadListInput = z.infer<typeof UploadListInput>

export interface ClassificationApi {
  /** every version of every list, newest first */
  listLists(): Promise<DataList[]>
  uploadList(input: UploadListInput): Promise<DataList>
  /** the people a classification workflow ran on, newest first, 60 a page */
  listClassificationRuns(workflowId: string, page?: number): Promise<ClassificationRunPage>
}
